import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";

import {
  Button,
  IconButton,
  Chip,
  Box,
  Typography,
  Popover,
  Tooltip,
} from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";

import { useLazyListCollectionQuery } from "store/services/collectionApi";

import { BaseTabs } from "components/Shared/BaseTabs";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import HighlightedText from "components/Shared/HighlightedText";
import TableFilter from "components/Shared/AppTable/TableFilter";
import ExportCollectionExcel from "./CollectionComponents/ExportCollectionExcel";

import { useAppMeta, useTablePagination } from "hooks";
import AppTableDateTime from "components/Shared/AppTableDateTime";

const ShipmentsCell = ({ shipments, keyword }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedShipments, setSelectedShipments] = useState([]);

  // Ensure shipments is an array; convert single object to array if needed
  const shipmentsArray = Array.isArray(shipments)
    ? shipments
    : shipments && shipments.shipmentId
      ? [shipments]
      : [];

  const visibleShipments = shipmentsArray.slice(0, 2);
  const remainingCount = shipmentsArray.length - visibleShipments.length;

  const handleChipClick = (event, allShipments) => {
    setAnchorEl(event.currentTarget);
    setSelectedShipments(allShipments);
  };

  const handleClose = () => setAnchorEl(null);

  const open = Boolean(anchorEl);

  return shipmentsArray.length > 0 ? (
    <>
      <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
        {visibleShipments.map((shipment, index) => {
          const shiId = shipment?.shipmentId
            ?.replace(/(.{4})/g, "$1-")
            .slice(0, -1);
          const displayId = shipment.IsReturning ? `${shiId} (R)` : shiId;
          return (
            <span key={index} style={{ fontSize: "0.9rem" }}>
              <HighlightedText text={displayId || "N/A"} highlight={keyword} />
              {index !== visibleShipments.length - 1 && ","}
            </span>
          );
        })}

        {remainingCount > 0 && (
          <Tooltip title="Click to view all Shipment IDs">
            <Chip
              label={`+${remainingCount}`}
              size="small"
              sx={{
                ml: 0.5,
                height: 18,
                fontSize: "10px",
                bgcolor: "#e0e0e0",
                color: "#333",
                cursor: "pointer",
              }}
              onClick={(e) => handleChipClick(e, shipmentsArray)}
            />
          </Tooltip>
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box p={2} minWidth={100}>
          {selectedShipments.map((shipment, index) => {
            const shiId = shipment?.shipmentId
              ?.replace(/(.{4})/g, "$1-")
              .slice(0, -1);
            const displayId = shipment.IsReturning ? `${shiId} (R)` : shiId;
            return (
              <Typography key={index} variant="body2" sx={{ wordBreak: "break-word" }}>
                <HighlightedText text={displayId || "N/A"} highlight={keyword} />
              </Typography>
            );
          })}
        </Box>
      </Popover>
    </>
  ) : (
    "N/A"
  );
};

const getStatusStyle = (status) => {
  const styles = {
    COMPLETED: { backgroundColor: "#EBF5FB", textColor: "green" },
    CANCELLED: { backgroundColor: "#FDEDEC", textColor: "#E74C3C" },
  };

  return styles[status?.toUpperCase()] || {
    backgroundColor: "#F8F9F9",
    textColor: "#7F8C8D",
  };
};

const CollectionListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Collections" }];

  const columns = useMemo(
    () => [
      {
        name: "ID",
        selector: (row) => row.collectionId,
        cell: (row) => (
          <Link
            to={`/app/crm/collection/${row._id}/detail`}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            {row?.collectionId ? (
              <HighlightedText text={row.collectionId} highlight={keyword} />
            ) : "N/A"}
          </Link>
        ),
      },
      {
        name: "Shipments",
        selector: (row) => row.shipmentId,
        cell: (row) => (
          <ShipmentsCell
            shipments={row.shipmentId} 
            keyword={keyword}
          />
        ),
      },
      {
        name: "Contractor Profile",
        selector: (row) => row.contractorId?.name || "N/A",
        cell: (row) =>
          `${row.contractorId?.name || "N/A"} (${row.contractorId?.contractorId || "N/A"})`,
      },
      {
        name: "Vehicle",
        selector: (row) => row.vehicle_id?.vehicle_id || "N/A",
      },
      {
        name: "Driver",
        selector: (row) => row.driverId?.name || "N/A",
        cell: (row) =>
          `${row.driverId?.name || "N/A"} (${row.driverId?.driverId || "N/A"})`,
      },
      {
        name: "Created By",
        selector: (row) => row.createdBy?.name || "N/A",
      },
      {
        name: "Created At",
        selector: (row) => <AppTableDateTime date={row?.createdAt} />,
      },
      {
        name: "Status",
        selector: (row) => row.status,
        cell: (row) => {
          const style = getStatusStyle(row.status);
          return (
            <Box display="flex" justifyContent="flex-end">
              <Chip
                label={row.status?.toUpperCase() || "N/A"}
                sx={{
                  backgroundColor: style.backgroundColor,
                  color: style.textColor,
                  fontWeight: "bold",
                  height: "17px",
                  borderRadius: "12px",
                  "& .MuiChip-label": {
                    fontSize: "0.6rem",
                    px: 1,
                    lineHeight: "14px",
                  },
                }}
              />
            </Box>
          );
        },
        right: true,
      },
      {
        name: "Actions",
        cell: (row) => {
          const isDelivered = Array.isArray(row.shipmentId)
            ? row.shipmentId.some((s) => s.status?.toUpperCase() === "DELIVERED")
            : row.shipmentId?.status?.toUpperCase() === "DELIVERED";

          return (
            <>
              <Link to={`/app/crm/collection/${row._id}/detail`}>
                <IconButton size="small" color="primary">
                  <InfoOutlined />
                </IconButton>
              </Link>

              {isDelivered ? (
                <Tooltip title="Shipment status is delivered">
                  <span>
                    <IconButton
                      size="small"
                      disabled
                      sx={{ color: "#B0BEC5", cursor: "not-allowed" }}
                    >
                      <ModeEditOutlineTwoTone />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Link to={`/app/crm/collection/${row._id}/edit`}>
                  <IconButton size="small">
                    <ModeEditOutlineTwoTone />
                  </IconButton>
                </Link>
              )}
            </>
          );
        },
        ignoreRowClick: true,
        right: true,
      },
    ],
    [keyword]
  );


  const [
    fetchFulfillments,
    { data: fulfillmentRes, isLoading, isFetching, error },
  ] = useLazyListCollectionQuery();

  useEffect(() => {
    fetchFulfillments({
      active: activeTab === "Active",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, perPage, keyword, fetchFulfillments]);

  useAppMeta(`${activeTab} Collections`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <BaseTabs
          tabs={[
            { label: "Active", value: "Active" },
            { label: "Inactive", value: "Inactive" },
          ]}
          defaultValue={activeTab}
          actions={<ExportCollectionExcel />}
        />
        <TableFilter
          searchLabel="Collection ID/ Shipment ID"
          disabled={isLoading || isFetching}
        />
        <AppTable
          columns={columns}
          data={fulfillmentRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={fulfillmentRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default CollectionListScreen;

const PageActions = () => (
  <Link to="/app/crm/collection/create">
    <Button startIcon={<Add />} variant="contained">
      New Collection
    </Button>
  </Link>
);